import { Platform, StyleSheet, useWindowDimensions, View, Text, Image, ScrollView, FlatList, SectionList, TouchableOpacity, TextInput, SafeAreaView } from 'react-native';
import React, { createContext, useContext, useMemo, memo, forwardRef } from 'react';
import { getStylesForProperty, getPropertyName } from 'css-to-react-native';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var colorAttributes = /*#__PURE__*/new Set(['backgroundColor', 'color', 'borderRightColor', 'borderBottomColor', 'borderColor', 'borderEndColor', 'borderLeftColor', 'backgroundColor', 'borderStartColor', 'borderTopColor', 'shadowColor', 'textDecorationColor']);
var lengthAttributes = /*#__PURE__*/new Set(['width', 'height', 'borderBottomEndRadius', 'borderBottomLeftRadius', 'borderBottomRightRadius', 'borderBottomStartRadius', 'borderBottomWidth', 'borderLeftWidth', 'borderRadius', 'borderRightWidth', 'borderTopEndRadius', 'borderTopLeftRadius', 'borderTopRightRadius', 'borderTopStartRadius', 'borderTopWidth', 'borderWidth', 'flexBasis', 'bottom', 'end', 'left', 'right', 'start', 'top', 'margin', 'marginBottom', 'marginEnd', 'marginHorizontal', 'marginLeft', 'marginRight', 'marginStart', 'marginTop', 'marginVertical', 'maxHeight', 'maxWidth', 'minHeight', 'minWidth', 'padding', 'paddingBottom', 'paddingEnd', 'paddingHorizontal', 'paddingLeft', 'paddingRight', 'paddingStart', 'paddingTop', 'paddingVertical', 'shadowOffset', 'shadowRadius', 'lineHeight', 'fontSize', 'letterSpacing']);

var ThemeContext = /*#__PURE__*/createContext(null);
var setThemeContext = function setThemeContext(ExternalThemeContext) {
  ThemeContext = ExternalThemeContext;
};
var useTheme = function useTheme() {
  var theme = useContext(ThemeContext);
  if (!theme) throw new Error('missing theme context, wrap your app in a ThemeProvider');
  return theme;
};
var withTheme = function withTheme(Component) {
  var ComponentWithTheme = function ComponentWithTheme(props) {
    var theme = useTheme();
    return React.createElement(Component, Object.assign({}, props, {
      theme: theme
    }), props.children || null);
  };

  ComponentWithTheme.displayName = "WithTheme(" + (Component.displayName || Component.name) + ")";
  return ComponentWithTheme;
};
var ThemeProvider = function ThemeProvider(_ref) {
  var theme = _ref.theme,
      children = _ref.children,
      rootCss = _ref.rootCss,
      _ref$rootFont = _ref.rootFont,
      rootFont = _ref$rootFont === void 0 ? '-apple-system, Roboto, sans-serif' : _ref$rootFont,
      _ref$rootBackgroundCo = _ref.rootBackgroundColor,
      rootBackgroundColor = _ref$rootBackgroundCo === void 0 ? 'white' : _ref$rootBackgroundCo,
      _ref$disableOutlines = _ref.disableOutlines,
      disableOutlines = _ref$disableOutlines === void 0 ? true : _ref$disableOutlines;
  // @ts-ignore cannot add colors to ThemeInterface because we don't want to restrict it
  var colors = theme.colors;
  var backgroundColor = rootBackgroundColor.substring(0, 1) === '$' ? colors[rootBackgroundColor.substring(1)] : rootBackgroundColor;
  return React.createElement(ThemeContext.Provider, {
    value: theme
  }, React.createElement(React.Fragment, null, Platform.OS === 'web' ? React.createElement("style", {
    // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
    dangerouslySetInnerHTML: {
      __html: "\n                html, body, #root {\n                  font-family: " + rootFont + ";\n                  min-height: 100%;\n                  background: " + backgroundColor + ";\n                  font-size: " + theme.rem + "px;\n                }\n                #root { display: flex; }\n                " + (disableOutlines ? 'textarea, select, input { outline: none; }' : '') + "\n                " + (rootCss || '') + "\n              "
    },
    key: "global_style"
  }) : null, children));
}; // css-to-react-native only supports proper css color values, so we resolve color variables to transparent hex values when compiling the template strings and then transform them back and dynamically resolve them from the theme when rendering

var currentColorId = 1;
var themeColors = {
  hexForVarName: /*#__PURE__*/new Map([]),
  nameForHex: /*#__PURE__*/new Map([])
};
var resolveColorVariablePlaceholder = function resolveColorVariablePlaceholder(variableName) {
  if (!themeColors.hexForVarName.has(variableName)) {
    var hexPlaceholder = "#" + (currentColorId++).toString(16).padStart(6, '0').toUpperCase() + "00";
    themeColors.hexForVarName.set(variableName, hexPlaceholder);
    themeColors.nameForHex.set(hexPlaceholder, variableName.substring(1));
  }

  return themeColors.hexForVarName.get(variableName);
}; // resolve any occurences of theme variables in the values of a style object

var plattformIsWeb = Platform.OS === 'web';
var resolveThemeVariables = function resolveThemeVariables(styleObject, theme, windowDimensions) {
  for (var key in styleObject) {
    if (key === 'elevation' && theme.elevation) {
      var shadowStyleObject = theme.elevation(styleObject[key]);

      for (var shadowKey in shadowStyleObject) {
        styleObject[shadowKey] = shadowStyleObject[shadowKey];
      }
    }

    if (key === 'cursor' && !plattformIsWeb) delete styleObject.cursor; // resolve all color names to theme variables if possible

    if (colorAttributes.has(key)) {
      var colorName = themeColors.nameForHex.get(styleObject[key]);

      if (colorName) {
        // @ts-ignore cannot add colors to ThemeInterface because we don't want to restrict it
        var colors = theme.colors;

        if (colorName in colors) {
          styleObject[key] = colors[colorName];
        } else {
          throw new Error("the color variable '$" + colorName + "' has not been defined in the theme.");
        }
      }
    } // resolve all rem and viewport units unless on web where they are supported natively


    if (!plattformIsWeb && lengthAttributes.has(key) && typeof styleObject[key] === 'string') {
      if (styleObject[key].includes('rem')) {
        styleObject[key] = Number.parseFloat(styleObject[key]) * theme.rem;
      } else if (styleObject[key].includes('vw')) {
        styleObject[key] = Number.parseFloat(styleObject[key]) * windowDimensions.width / 100;
      } else if (styleObject[key].includes('vh')) {
        styleObject[key] = Number.parseFloat(styleObject[key]) * windowDimensions.height / 100;
      }
    }
  }

  return styleObject;
};

var cssCommentRegexp = /*#__PURE__*/new RegExp('\\/\\*[^]+?\\*\\/', 'g');

var resolveTemplateLiteral = function resolveTemplateLiteral(strings, expressions, props) {
  return strings.map(function (str, i) {
    // resolve expressions
    var exp = expressions.length > i ? expressions[i] : '';

    if (typeof exp === 'string' || typeof exp === 'number') {
      return str + exp;
    } else {
      return str + exp(props);
    }
  }).join('') // remove comments
  .replace(cssCommentRegexp, '');
}; // create a RN style object from a single css declaration


var variableRegexp = /*#__PURE__*/new RegExp('\\$[\\w|-]+', 'g');
var styleObjectCache = /*#__PURE__*/new Map([]);

var createStyleObject = function createStyleObject(cssDeclaration) {
  if (!cssDeclaration) return {};
  var styleObject = styleObjectCache.get(cssDeclaration);

  if (!styleObject) {
    styleObject = Object.assign.apply(Object, [{}].concat(cssDeclaration.split(';').map(function (rule) {
      return rule.split(':').map(function (value) {
        return value && value.trim();
      }).filter(function (value) {
        return value;
      });
    }).filter(function (rule) {
      return rule.length === 2;
    }).map(function (_ref) {
      var name = _ref[0],
          value = _ref[1];
      var resolvedValue = value.replace(variableRegexp, function (variable) {
        return resolveColorVariablePlaceholder(variable);
      });

      try {
        return getStylesForProperty(getPropertyName(name), resolvedValue);
      } catch (e) {
        throw new Error("could not parse '" + name + ": " + value + "'\n" + e.message);
      }
    })));
    styleObjectCache.set(cssDeclaration, styleObject);
  }

  return styleObject;
}; // create an object of RN style objects from a single css declaration


var nestedStyleObjectsCache = /*#__PURE__*/new Map([]);
var nestedDeclarationRegExp = /*#__PURE__*/new RegExp('\\w+\\s*\\{([^]+?)\\}', 'g');

var createNestedStyleObject = function createNestedStyleObject(nestedCssDeclaration) {
  var nestedStyleObjects = nestedStyleObjectsCache.get(nestedCssDeclaration);

  if (!nestedStyleObjects) {
    var mainStyleDeclaration = nestedCssDeclaration;
    nestedStyleObjects = {};
    var matches;

    while (matches = nestedDeclarationRegExp.exec(nestedCssDeclaration)) {
      var _matches = matches,
          string = _matches[0],
          content = _matches[1];
      var declaration = content.trim();
      var name = string.split('{')[0].trim();
      nestedStyleObjects[name + "Style"] = createStyleObject(declaration);
      mainStyleDeclaration = mainStyleDeclaration.replace(string, '');
    }

    nestedStyleObjects.style = createStyleObject(mainStyleDeclaration.trim());
    nestedStyleObjectsCache.set(nestedCssDeclaration, nestedStyleObjects);
  }

  return nestedStyleObjects;
}; // generate styleSheet from nested style Object


var useStyleSheet = function useStyleSheet(styles, theme) {
  var windowDimensions = useWindowDimensions();
  return useMemo(function () {
    // we need to make sure to do a deep clone here, so that theme and viewport units can be resolved from original strings
    var stylesCopy = _extends({}, styles);

    for (var key in stylesCopy) {
      stylesCopy[key] = resolveThemeVariables(_extends({}, stylesCopy[key]), theme, windowDimensions);
    }

    return StyleSheet.create(stylesCopy);
  }, [styles, theme, windowDimensions]);
};

var makeTemplateFunction = function makeTemplateFunction(Component, transformProps, filterComponentProps) {
  return function (strings) {
    for (var _len = arguments.length, expressions = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      expressions[_key - 1] = arguments[_key];
    }

    var displayName = "Styled(" + (Component.displayName || Component.name) + ")";
    var styledForwardRefRenderFunction;

    if (expressions.every(function (exp) {
      return typeof exp === 'string';
    })) {
      // if no props are used in the styles, then we can statically generate the cssString
      var cssString = resolveTemplateLiteral(strings, expressions);
      var styles = createNestedStyleObject(cssString);

      styledForwardRefRenderFunction = function styledForwardRefRenderFunction( // @ts-ignore ts doesn't understand that A is not allwoed to declar children and style
      _ref2, ref) {
        var children = _ref2.children,
            style = _ref2.style,
            props = _objectWithoutPropertiesLoose(_ref2, ["children", "style"]);

        var theme = useTheme();
        var styleProps = useStyleSheet(styles, theme);
        styleProps = style ? _extends({}, styleProps, {
          style: [styleProps.style, style]
        }) : styleProps;
        var transformedProps = transformProps(_extends({}, props, {
          theme: theme
        }));
        return React.createElement(Component, Object.assign({}, filterComponentProps(transformedProps), styleProps, {
          ref: ref
        }), children);
      };
    } else {
      // if the cssString depends on props, we can at least ignore changes to children
      styledForwardRefRenderFunction = function styledForwardRefRenderFunction( // @ts-ignore ts doesn't understand that A is not allwoed to declar children and style
      _ref3, ref) {
        var children = _ref3.children,
            style = _ref3.style,
            props = _objectWithoutPropertiesLoose(_ref3, ["children", "style"]);

        var theme = useTheme();
        var transformedProps = transformProps(_extends({}, props, {
          theme: theme
        }));
        var cssString = useMemo(function () {
          return resolveTemplateLiteral(strings, expressions, transformedProps);
        }, [props, theme]);
        var styles = useMemo(function () {
          return createNestedStyleObject(cssString);
        }, [cssString]);
        var styleProps = useStyleSheet(styles, theme);
        styleProps = style ? _extends({}, styleProps, {
          style: [styleProps.style, style]
        }) : styleProps;
        return React.createElement(Component, Object.assign({}, filterComponentProps(transformedProps), styleProps, {
          ref: ref
        }), children);
      };
    }

    styledForwardRefRenderFunction.displayName = displayName; // @ts-ignore ts gets confused in HOC wrappers but the declared return type has the proper refs

    return memo(forwardRef(styledForwardRefRenderFunction));
  };
};
var useStyle = function useStyle(cssDeclaration) {
  var theme = useTheme();
  var styles = useMemo(function () {
    return {
      generated: createStyleObject(cssDeclaration.trim())
    };
  }, []);
  return useStyleSheet(styles, theme).generated;
};

var useLengthAttribute = function useLengthAttribute(margin) {
  var theme = useTheme();
  var windowDimensions = useWindowDimensions();
  return useMemo(function () {
    var parsedMargin = margin.replace(/\s\s+/g, ' ').split(' ');
    var pixelValues = parsedMargin.map(function (str) {
      if (!str || typeof str === 'number') return str;
      if (typeof str !== 'string') throw new Error("expected " + str + " to be a string");
      var value = Number.parseFloat(str);
      if (value === 0) return 0;
      var unit = str.trim().replace(String(value), '');
      if (!unit) throw new Error("length string '" + str + "' contains no unit");

      switch (unit) {
        case 'rem':
          return value * theme.rem;

        case 'px':
          return value;

        case 'vw':
          if (!windowDimensions) throw new Error('pass windowDimensions as the 3rd argument');
          return value * windowDimensions.width / 100;

        case 'vh':
          if (!windowDimensions) throw new Error('pass windowDimensions as the 3rd argument');
          return value * windowDimensions.height / 100;

        default:
          throw new Error("cannot parse length string '" + str + "', unknown unit '" + unit + "'");
      }
    });
    return [0, 1, 2, 3].map(function (i) {
      return pixelValues[i] !== undefined ? pixelValues[i] : pixelValues[i - 2] !== undefined ? pixelValues[i - 2] : pixelValues[0] || 0;
    });
  }, [margin, theme.rem, windowDimensions]);
};
var useColorAttribute = function useColorAttribute(color) {
  var theme = useTheme(); // @ts-ignore cannot add colors to ThemeInterface because we don't want to restrict it

  var colors = theme.colors;
  return color in colors ? colors[color] : color && color.substring(1) in colors ? colors[color.substring(1)] : color;
};

var accessibilityPropNames = ['accessible', 'accessibilityActions', 'accessibilityLabel', 'accessibilityRole', 'accessibilityState', 'accessibilityHint', 'accessibilityValue', 'onAccessibilityAction', 'accessibilityComponentType', 'accessibilityLiveRegion', 'importantForAccessibility', 'accessibilityElementsHidden', 'accessibilityTraits', 'accessibilityViewIsModal', 'onAccessibilityEscape', 'onAccessibilityTap', 'onMagicTap', 'accessibilityIgnoresInvertColors']; // this ensures that we get typescript errors if a key from ViewProps is missing

var accessibilityPropNamesWithEnsuredCompleteKeys = accessibilityPropNames;
var viewPropNames = /*#__PURE__*/['style'].concat(accessibilityPropNamesWithEnsuredCompleteKeys, ['onStartShouldSetResponder', 'hitSlop', 'nativeID', 'onLayout', 'onMoveShouldSetResponder', 'onMoveShouldSetResponderCapture', 'onResponderGrant', 'onResponderMove', 'onResponderReject', 'onResponderRelease', 'onResponderTerminate', 'onResponderTerminationRequest', 'onStartShouldSetResponderCapture', 'pointerEvents', 'removeClippedSubviews', 'testID', 'collapsable', 'needsOffscreenAlphaCompositing', 'renderToHardwareTextureAndroid', 'shouldRasterizeIOS', 'focusable', 'isTVSelectable', 'hasTVPreferredFocus', 'tvParallaxProperties', 'tvParallaxShiftDistanceX', 'tvParallaxShiftDistanceY', 'tvParallaxTiltAngle', 'tvParallaxMagnification', 'onResponderEnd', 'onResponderStart', 'onTouchStart', 'onTouchMove', 'onTouchEnd', 'onTouchCancel', 'onTouchEndCapture']); // this ensures that we get typescript errors if a key from ViewProps is missing

var viewPropNamesWithEnsuredCompleteKeys = viewPropNames;
var viewProps = /*#__PURE__*/new Set(viewPropNamesWithEnsuredCompleteKeys);
var textPropNames = /*#__PURE__*/['style'].concat(accessibilityPropNamesWithEnsuredCompleteKeys, ['selectable', 'ellipsizeMode', 'nativeID', 'numberOfLines', 'onLayout', 'onLongPress', 'onPress', 'pressRetentionOffset', 'allowFontScaling', 'testID', 'disabled', 'selectionColor', 'textBreakStrategy', 'adjustsFontSizeToFit', 'minimumFontScale', 'suppressHighlighting', 'pointerEvents', 'lineBreakMode', 'maxFontSizeMultiplier']); // this ensures that we get typescript errors if a key from ViewProps is missing

var textPropNamesWithEnsuredCompleteKeys = textPropNames;
var textProps = /*#__PURE__*/new Set(textPropNamesWithEnsuredCompleteKeys);
var imagePropNames = /*#__PURE__*/['style'].concat(accessibilityPropNamesWithEnsuredCompleteKeys, ['blurRadius', 'onLayout', 'onLoad', 'onLoadEnd', 'onLoadStart', 'resizeMode', 'source', 'loadingIndicatorSource', 'onError', 'testID', 'resizeMethod', 'capInsets', 'defaultSource', 'onPartialLoad', 'onProgress', 'fadeDuration', 'pointerEvents', 'progressiveRenderingEnabled']); // this ensures that we get typescript errors if a key from ViewProps is missing

var imagePropNamesWithEnsuredCompleteKeys = imagePropNames;
var imageProps = /*#__PURE__*/new Set(imagePropNamesWithEnsuredCompleteKeys);
var listPropNames = /*#__PURE__*/[].concat(viewPropNamesWithEnsuredCompleteKeys, ['columnWrapperStyle', 'contentContainerStyle', 'indicatorStyle'], accessibilityPropNamesWithEnsuredCompleteKeys, ['renderItem', 'data', 'sections', 'ItemSeparatorComponent', 'ListEmptyComponent', 'ListFooterComponent', 'ListHeaderComponent', 'extraData', 'getItemLayout', 'horizontal', 'initialNumToRender', 'initialScrollIndex', 'inverted', 'keyExtractor', 'numColumns', 'onEndReached', 'onEndReachedThreshold', 'onRefresh', 'onViewableItemsChanged', 'progressViewOffset', 'legacyImplementation', 'refreshing', 'viewabilityConfig', 'viewabilityConfigCallbackPairs', 'renderSectionFooter', 'renderSectionHeader', 'SectionSeparatorComponent', 'stickySectionHeadersEnabled', 'updateCellsBatchingPeriod', 'windowSize', 'disableVirtualization', 'getItem', 'getItemCount', 'debug', 'CellRendererComponent', 'onScrollToIndexFailed', 'renderScrollComponent', 'maxToRenderPerBatch', 'alwaysBounceVertical', 'keyboardDismissMode', 'keyboardShouldPersistTaps', 'onContentSizeChange', 'onMomentumScrollBegin', 'onMomentumScrollEnd', 'onScroll', 'onScrollBeginDrag', 'onScrollEndDrag', 'pagingEnabled', 'refreshControl', 'scrollEnabled', 'showsHorizontalScrollIndicator', 'showsVerticalScrollIndicator', 'stickyHeaderIndices', 'endFillColor', 'overScrollMode', 'scrollPerfTag', 'alwaysBounceHorizontal', 'automaticallyAdjustContentInsets', 'bounces', 'bouncesZoom', 'canCancelContentTouches', 'centerContent', 'contentInset', 'contentInsetAdjustmentBehavior', 'contentOffset', 'decelerationRate', 'directionalLockEnabled', 'maximumZoomScale', 'minimumZoomScale', 'pinchGestureEnabled', 'scrollEventThrottle', 'scrollIndicatorInsets', 'scrollsToTop', 'snapToAlignment', 'snapToInterval', 'snapToOffsets', 'snapToStart', 'snapToEnd', 'zoomScale', 'nestedScrollEnabled', 'listKey', 'invertStickyHeaders', 'disableIntervalMomentum', 'disableScrollViewPanResponder', 'onScrollAnimationEnd', 'scrollToOverflowEnabled', 'onScrollToTop', 'fadingEdgeLength', 'persistentScrollbar']); // this ensures that we get typescript errors if a key from ViewProps is missing

var listPropNamesWithEnsuredCompleteKeys = listPropNames;
var listProps = /*#__PURE__*/new Set(listPropNamesWithEnsuredCompleteKeys);
var inputPropNames = /*#__PURE__*/[].concat(viewPropNamesWithEnsuredCompleteKeys, accessibilityPropNamesWithEnsuredCompleteKeys, ['allowFontScaling', 'autoCapitalize', 'autoCorrect', 'autoFocus', 'blurOnSubmit', 'caretHidden', 'clearButtonMode', 'clearTextOnFocus', 'contextMenuHidden', 'dataDetectorTypes', 'defaultValue', 'disableFullscreenUI', 'editable', 'enablesReturnKeyAutomatically', 'inlineImageLeft', 'inlineImagePadding', 'keyboardAppearance', 'keyboardType', 'maxLength', 'multiline', 'numberOfLines', 'onBlur', 'onChange', 'onChangeText', 'onContentSizeChange', 'onEndEditing', 'onFocus', 'onKeyPress', 'onScroll', 'onSelectionChange', 'onSubmitEditing', 'placeholder', 'placeholderTextColor', 'returnKeyLabel', 'returnKeyType', 'scrollEnabled', 'secureTextEntry', 'selection', 'selectionColor', 'selectionState', 'selectTextOnFocus', 'spellCheck', 'textContentType', 'textBreakStrategy', 'underlineColorAndroid', 'value', 'maxFontSizeMultiplier', 'onTextInput', 'inputAccessoryViewID', 'passwordRules', 'rejectResponderTermination', 'autoCompleteType', 'importantForAutofill', 'textAlignVertical', 'showSoftInputOnFocus']); // this ensures that we get typescript errors if a key from ViewProps is missing

var inputPropNamesWithEnsuredCompleteKeys = inputPropNames;
var inputProps = /*#__PURE__*/new Set(inputPropNamesWithEnsuredCompleteKeys);
var pressablePropNames = /*#__PURE__*/[].concat(viewPropNamesWithEnsuredCompleteKeys, accessibilityPropNamesWithEnsuredCompleteKeys, ['delayLongPress', 'delayPressIn', 'delayPressOut', 'disabled', 'onBlur', 'onFocus', 'onLongPress', 'onPress', 'onPressIn', 'onPressOut', 'pressRetentionOffset', 'activeOpacity', 'children', 'android_disableSound', 'android_ripple', 'testOnly_pressed', 'touchSoundDisabled']); // this ensures that we get typescript errors if a key from ViewProps is missing

var pressablePropNamesWithEnsuredCompleteKeys = pressablePropNames;
var pressableProps = /*#__PURE__*/new Set(pressablePropNamesWithEnsuredCompleteKeys);
var domProps = /*#__PURE__*/new Set(['allowFullScreen', 'autoComplete', 'autoFocus', 'challenge', 'charSet', 'checked', 'className', 'content', 'contentEditable', 'crossOrigin', 'data', 'disabled', 'draggable', 'href', 'id', 'scrolling', 'spellCheck', 'tabIndex', 'target', 'type', 'wrap', 'onCopy', 'onCut', 'onPaste', 'onCompositionEnd', 'onCompositionStart', 'onCompositionUpdate', 'onKeyDown', 'onKeyPress', 'onKeyUp', 'onFocus', 'onBlur', 'onChange', 'onInput', 'onInvalid', 'onSubmit', 'onClick', 'onContextMenu', 'onDoubleClick', 'onDrag', 'onDragEnd', 'onDragEnter', 'onDragExit', 'onDragLeave', 'onDragOver', 'onDragStart', 'onDrop', 'onMouseDown', 'onMouseEnter', 'onMouseLeave', 'onMouseMove', 'onMouseOut', 'onMouseOver', 'onMouseUp', 'onPointerDown', 'onPointerMove', 'onPointerUp', 'onPointerCancel', 'onGotPointerCapture', 'onLostPointerCapture', 'onPointerEnter', 'onPointerLeave', 'onPointerOver', 'onPointerOut', 'onSelect', 'onTouchCancel', 'onTouchEnd', 'onTouchMove', 'onTouchStart', 'onScroll', 'onWheel', 'onAnimationStart', 'onAnimationEnd', 'onAnimationIteration', 'onTransitionEnd', 'onToggle']);
var makePropsFilter = function makePropsFilter() {
  var allPropsSet = new Set([]);

  for (var _len = arguments.length, propsSets = new Array(_len), _key = 0; _key < _len; _key++) {
    propsSets[_key] = arguments[_key];
  }

  for (var _i = 0, _propsSets = propsSets; _i < _propsSets.length; _i++) {
    var set = _propsSets[_i];
    set.forEach(function (el) {
      return allPropsSet.add(el);
    });
  }

  return function (props) {
    var propsCopy = {};

    for (var key in props) {
      if (allPropsSet.has(key)) propsCopy[key] = props[key];
    }

    return propsCopy;
  };
};

var styled = function styled(Component, filterComponentProps) {
  if (filterComponentProps === void 0) {
    filterComponentProps = function filterComponentProps(p) {
      return p;
    };
  }

  var templateFunction = makeTemplateFunction(Component, function (props) {
    return props;
  }, filterComponentProps); // it is impossible to infer the return value of attrMaker, it has to be typed explicitly

  templateFunction.attrs = function (attrMaker) {
    var transformProps = function transformProps(props) {
      return _extends({}, props, attrMaker instanceof Function ? attrMaker(props) : attrMaker);
    };

    return makeTemplateFunction(Component, transformProps, filterComponentProps);
  };

  return templateFunction;
};

styled.View = /*#__PURE__*/styled(View, /*#__PURE__*/makePropsFilter(viewProps, domProps));
styled.Text = /*#__PURE__*/styled(Text, /*#__PURE__*/makePropsFilter(textProps, domProps));
styled.Image = /*#__PURE__*/styled(Image, /*#__PURE__*/makePropsFilter(imageProps, domProps));
styled.ScrollView = /*#__PURE__*/styled(ScrollView, /*#__PURE__*/makePropsFilter(listProps, domProps));
styled.FlatList = /*#__PURE__*/styled(FlatList, /*#__PURE__*/makePropsFilter(listProps, domProps));
styled.SectionList = /*#__PURE__*/styled(SectionList, /*#__PURE__*/makePropsFilter(listProps, domProps));
styled.TouchableOpacity = /*#__PURE__*/styled(TouchableOpacity, /*#__PURE__*/makePropsFilter(pressableProps, domProps));
styled.TextInput = /*#__PURE__*/styled(TextInput, /*#__PURE__*/makePropsFilter(inputProps, viewProps, textProps, domProps));
styled.SafeAreaView = /*#__PURE__*/styled(SafeAreaView, /*#__PURE__*/makePropsFilter(viewProps, domProps));
var filterProps = /*#__PURE__*/makePropsFilter(viewProps, textProps, imageProps, listProps, pressableProps, inputProps, domProps);

export default styled;
export { ThemeContext, ThemeProvider, filterProps, setThemeContext, useColorAttribute, useLengthAttribute, useStyle, useTheme, withTheme };
//# sourceMappingURL=styled-native-components.esm.js.map
